import Commission from "../../../classes/Commission";
import ManagerShare from "../../../classes/ManagerShare";
import Paycheck from "../../../classes/Paycheck";
import { db } from "../../../boot/firebase";
import { collection, doc, documentId, writeBatch } from "firebase/firestore";
import { groupBy } from "lodash";
import { formatCurrency } from "../../../util/misc";

export const GET_COMMISSION = ({ dispatch }, payload) => {
  const path =
    typeof payload === "string"
      ? payload
      : Commission.getPath(payload.userID, payload.commissionID || payload.id);
  const stateID = Commission.getStateID(path);
  const commissionID = path.split("/").pop();

  return dispatch("GET_DOC", {
    converter: Commission,
    path,
    stateKey: "commission",
    stateID,
    id: commissionID,
  });
};

export const GET_COMMISSIONS = (
  { dispatch },
  { conditions, limitCount, pagination }
) => {
  return dispatch("GET_COLLECTION", {
    converter: Commission,
    stateKey: "commission",
    colGroup: "commission",
    conditions,
    limitCount,
    pagination,
  });
};

export const LISTEN_COMMISSION = (
  { dispatch },
  { userID, commissionID, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: Commission,
    path: Commission.getPath(userID, commissionID),
    stateKey: "commission",
    id: commissionID,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_COMMISSIONS = (
  { dispatch },
  { userID, conditions, pagination, next, error, unlisten }
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: Commission,
    colGroup: userID ? null : "commission",
    path: userID ? Commission.getPath(userID) : null,
    stateKey: "commission",
    conditions,
    pagination,
    next,
    error,
    unlisten,
  });
};

/**
 * Listen to the commission records a paycheck pays, so its card can price
 * each sale. The paycheck dialogs open from pages that load no commissions
 * (Paychecks, a Tx, a contact balance), and without the records every line
 * read $0.00. Its own listener path keeps it from sharing -- and narrowing --
 * the user-wide listener the Commission page holds. Resolves to `null` for a
 * paycheck with no sales, since an empty `in` query is invalid.
 */
export const LISTEN_PAYCHECK_COMMISSIONS = (
  { dispatch },
  { paycheck, error }
) => {
  if (!paycheck || !paycheck.userID || !paycheck.saleIDs.length) {
    return Promise.resolve(null);
  }
  return dispatch("LISTEN_COLLECTION", {
    converter: Commission,
    path: Commission.getPath(paycheck.userID),
    listenerPath: `paycheck-commission-${paycheck.userID}-${paycheck.id}`,
    stateKey: "commission",
    conditions: [documentId(), "in", paycheck.saleIDs],
    error,
  });
};

/**
 * Manually mark commissions and/or manager shares as paid (admin cleanup
 * tool for reconciling historical payroll). Creates a paycheck per
 * (payroll, recipient) combination for audit trail, then directly updates
 * each commission / manager share document — mirroring the touchPaycheck
 * Cloud Function logic client-side.
 *
 * Commissions and manager shares may belong to different users (e.g. a
 * location manager receives shares from sales made by other salespeople),
 * so records within a group are further split by `userID` and a separate
 * paycheck is created for each user.
 *
 * Accepts two signatures:
 *
 * 1. Single-group (legacy):
 *      { commissions, managerShares?, amountPaid, payrollDate, payrollNumber }
 *    Creates one paycheck per employee.
 *
 * 2. Multi-group:
 *      { groups: [{ commissions, managerShares?, amountPaid, payrollDate, payrollNumber }, ...] }
 *    Creates one paycheck per (group × employee) combination. All writes
 *    across all groups are consolidated into the minimum number of
 *    Firestore batches (<= 500 writes each), committed in parallel.
 */
export const MARK_COMMISSIONS_PAID = async ({ state }, payload) => {
  const MAX_BATCH_WRITES = 500;
  const modified = new Date();
  const modifiedBy = state.user.id;

  const groups = payload.groups || [payload];

  // Each "write unit" is the atomic set of writes for one paycheck:
  // 1 paycheck `set` + its associated commission / manager share
  // `update`s. We keep these together in the same Firestore batch so a
  // partial failure cannot orphan a paycheck whose records weren't marked
  // paid (or vice versa).
  const units = [];

  for (const group of groups) {
    const {
      commissions,
      managerShares = [],
      amountPaid,
      payrollDate,
      payrollNumber,
    } = group;

    // Defensive: only commissions that are ready for approval or approved
    // may be reconciled. Expected / partiallyReady commissions lack actual
    // pretax/cost data and would contribute $0 to the paycheck total;
    // already-paid records should not be re-reconciled.
    const invalid = commissions.filter(
      (c) => c.status !== "unapproved" && c.status !== "approved"
    );
    if (invalid.length) {
      throw new Error(
        `${
          invalid.length
        } of the selected commission(s) matched a paid payroll record but ${
          invalid.length === 1 ? "isn't" : "aren't"
        } yet marked ready to be paid in the app.`
      );
    }

    // Manager shares must have actual pretax data and not already be paid.
    const invalidShares = managerShares.filter(
      (s) => s.data.paid === true || s.data.actualPreTax === null
    );
    if (invalidShares.length) {
      throw new Error(
        `${invalidShares.length} selected manager share(s) are not ready to be reconciled (must have actual pre-tax data and not already be paid).`
      );
    }

    // Group both record types by userID. Each user gets exactly one
    // paycheck that captures all of their commissions and all of their
    // manager shares in this payroll period.
    const commissionsByUser = groupBy(commissions, "userID");
    const sharesByUser = groupBy(managerShares, "userID");
    const userIDs = Array.from(
      new Set([...Object.keys(commissionsByUser), ...Object.keys(sharesByUser)])
    );

    // Sum contributions across all users for proportional amountPaid
    // splitting. Uses remainingCommission for commissions — the true-up
    // after any pay-app advances already approved on the sale, so a
    // reconciled payout cannot pay those advances twice — and actualAmount
    // (sharePercent × actualPreTax) for manager shares.
    const contribution = (userID) => {
      const cSum = (commissionsByUser[userID] || []).reduce(
        (sum, c) => sum + (c.remainingCommission || 0),
        0
      );
      const sSum = (sharesByUser[userID] || []).reduce(
        (sum, s) => sum + (s.actualAmount || 0),
        0
      );
      return { cSum, sSum, total: cSum + sSum };
    };
    const grandTotal = userIDs.reduce(
      (sum, uid) => sum + contribution(uid).total,
      0
    );

    // A clawback makes `remainingCommission` negative, so a user's net can
    // come out below zero. This path writes the paycheck as a raw batch
    // `set` -- no Paycheck instance, so none of Paycheck.rules runs -- and
    // stamps it `locked: true`. Left unguarded it posts a NEGATIVE, locked
    // paycheck and closes the commissions out at the same time.
    //
    // No paycheck should ever go negative, and recovery is Marcia's call to
    // make deliberately: Craig, "it's up to the payroll role to decide how
    // much of that negative balance to claw back per paycheck... so it can
    // be spread across multiple paychecks if needed." So block the payoff
    // and name who needs handling rather than guessing at an amount.
    const underwater = userIDs
      .map((uid) => ({ uid, total: contribution(uid).total }))
      .filter(({ total }) => total < 0);
    if (underwater.length) {
      const named = underwater.map(({ uid, total }) => {
        const user =
          (commissionsByUser[uid] || [])[0]?.user ||
          (sharesByUser[uid] || [])[0]?.user;
        return `${user?.name || uid} (${formatCurrency(total)})`;
      });
      throw new Error(
        `${named.join(", ")} ${
          underwater.length === 1 ? "nets" : "net"
        } less than $0 on this payroll, which would create a negative ` +
          `paycheck. Remove the clawed-back sale(s) from this payroll and ` +
          `recover the balance through held pay, where it can be spread ` +
          `across multiple paychecks.`
      );
    }

    // With no negative totals left, a non-positive grand total means every
    // selected record contributes $0 -- there is nothing to apportion
    // `amountPaid` against, and the old fallback handed EVERY user the full
    // amount.
    if (grandTotal <= 0) {
      throw new Error(
        `The selected commission records total ${formatCurrency(
          grandTotal
        )}, so there is nothing to pay out. Check the selection before ` +
          `reconciling this payroll.`
      );
    }

    for (const userID of userIDs) {
      const userCommissions = commissionsByUser[userID] || [];
      const userShares = sharesByUser[userID] || [];

      const paycheckRef = doc(collection(db, Paycheck.getPath(userID)));

      const {
        cSum: amountCommission,
        sSum: amountManagerShare,
        total,
      } = contribution(userID);

      // Split amountPaid proportionally across users by total earnings.
      // grandTotal > 0 and every `total` >= 0, both guaranteed above.
      const employeeAmountPaid =
        Math.round((total / grandTotal) * amountPaid * 100) / 100;

      const unit = [];

      // Paycheck record for audit trail
      unit.push({
        type: "set",
        ref: paycheckRef,
        data: {
          amount: employeeAmountPaid,
          amountCommission,
          amountManagerShare,
          amountDraw: 0,
          amountFromHeld: 0,
          amountToHeld: 0,
          commission: userCommissions.map((c) => c.id),
          managerShares: userShares.map((s) => s.id),
          override: null,
          overrideReason: "",
          overrideConfirmed: false,
          payment: null,
          payrollDate,
          payrollNumber,
          locked: true,
          created: modified,
          createdBy: modifiedBy,
          modified: null,
          modifiedBy: null,
        },
      });

      // Mark each commission as paid (mirrors touchPaycheck Cloud Function)
      for (const c of userCommissions) {
        unit.push({
          type: "update",
          ref: doc(db, Commission.getPath(userID, c.id)),
          data: {
            paycheck: paycheckRef.id,
            paid: true,
            locked: true,
            modified,
            modifiedBy,
          },
        });
      }

      // Mark each manager share as paid
      for (const s of userShares) {
        unit.push({
          type: "update",
          ref: doc(db, ManagerShare.getPath(userID, s.id)),
          data: {
            paycheck: paycheckRef.id,
            paid: true,
            modified,
            modifiedBy,
          },
        });
      }

      if (unit.length > MAX_BATCH_WRITES) {
        throw new Error(
          `Too many records selected for a single user. Maximum ${
            MAX_BATCH_WRITES - 1
          } records per user per payout.`
        );
      }

      units.push(unit);
    }
  }

  // Greedy-pack units into batches of <= 500 writes. Each unit stays
  // entirely within one batch to preserve paycheck/record atomicity.
  const batches = [];
  let current = writeBatch(db);
  let currentSize = 0;
  for (const unit of units) {
    if (currentSize > 0 && currentSize + unit.length > MAX_BATCH_WRITES) {
      batches.push(current.commit());
      current = writeBatch(db);
      currentSize = 0;
    }
    for (const w of unit) {
      if (w.type === "set") current.set(w.ref, w.data);
      else current.update(w.ref, w.data);
    }
    currentSize += unit.length;
  }
  if (currentSize > 0) batches.push(current.commit());

  await Promise.all(batches);
};
